import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Logo, Icon, Button, Chip, Alert, Spinner, portalChrome } from '@glplus/glpweb-dsl';
import { useSetup } from '../lib/SetupContext';
import { useVendor } from '../lib/CapabilitiesContext';
import type { SetupStep } from '../lib/setup';
import { vendorApiClient } from '../lib/vendorApiClient';

/**
 * §9 Setup wizard (Figma 420:4 / 441:2 / 444:2 / 446:4) — a full-screen flow
 * with its own "Vendor · Setup" chrome. The rail is 7 steps: the application's
 * four reviewed application sections arrive PRE-FILLED and view-only, then the
 * vendor works Storefront → Import → Confirm. Storefront persists to the profile;
 * Import + Confirm reproduce the Figma as previews (backend deferred). Changes
 * to reviewed application details are requested through Help instead of being
 * written directly from this wizard.
 */

const CHROME = portalChrome.vendor;
const GREEN = '#25862D';
const ORANGE = '#F67A1A';
const GREY = '#9aa5b1';
const ACTIVE_START = 4; // index of the first "active" step (Storefront)

export default function SetupWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { progress } = useSetup();
  const [active, setActive] = useState(() => Math.max(progress.firstIncomplete, ACTIVE_START));

  const total = progress.steps.length;
  const pct = Math.round((progress.completed / total) * 100);
  const activeKey = progress.steps[active]?.key ?? 'storefront';

  const exit = () => navigate('/');
  const next = () => (active < total - 1 ? setActive(active + 1) : exit());
  const back = () => active > ACTIVE_START && setActive(active - 1);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f6f8' }}>
      {/* Wizard chrome */}
      <Box component="header" sx={{ background: { xs: CHROME.headerBgMobile, md: CHROME.headerBg }, color: '#fff', minHeight: 64, px: { xs: '16px', md: '40px' }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
          <Logo product="vendor" height={48} />
          <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '15px' }}>{t('setupWizard.chrome')}</Typography>
        </Box>
        <Box component="button" type="button" onClick={exit} sx={{ ml: 'auto', border: 'none', background: 'transparent', color: '#fff', fontWeight: 700, fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}>
          {t('setupWizard.saveExit')}
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, px: { xs: '16px', md: '40px' }, py: { xs: 3, md: 4 }, maxWidth: 1280, mx: 'auto', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>{t('setupWizard.title')}</Typography>
          <Typography sx={{ color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {t('setupWizard.stepN', { n: active + 1, total })}
          </Typography>
        </Box>

        {/* Progress bar */}
        <Box sx={{ mt: 1.5, mb: 3, height: 6, borderRadius: 999, bgcolor: '#e2e7ec', overflow: 'hidden' }}>
          <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: GREEN, transition: 'width 0.2s ease' }} />
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'flex-start' }}>
          {/* Step rail */}
          <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: 300 }, bgcolor: '#fff', border: '1px solid #e2ebe4', borderRadius: 3, p: 1.5 }}>
            {progress.steps.map((s, i) => (
              <RailItem key={s.key} step={s} index={i} active={i === active} onSelect={() => (s.kind === 'active' ? setActive(i) : undefined)} />
            ))}
          </Box>

          {/* Current step content */}
          <Box sx={{ flex: 1, minWidth: 0, bgcolor: '#fff', border: '1px solid #e2ebe4', borderRadius: 3, p: 3 }}>
            {activeKey === 'storefront' && <StorefrontStep />}
            {activeKey === 'import' && <ImportStep />}
            {activeKey === 'confirm' && <ConfirmStep />}
          </Box>
        </Box>

        {/* Footer nav */}
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 3 }}>
          <Button ariaLabel={t('common.back')} buttonText={t('common.back')} variant="outlinedGreen" onClick={back} disabled={active <= ACTIVE_START} />
          <Box sx={{ ml: 'auto' }}>
            <Button ariaLabel={t('common.continue')} buttonText={active === total - 1 ? t('setupWizard.finish') : t('common.continue')} variant="contained" onClick={next} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
// ─── Rail item ───────────────────────────────────────────────────────────────

function RailItem({ step, index, active, onSelect }: { step: SetupStep; index: number; active: boolean; onSelect: () => void }) {
  const { t } = useTranslation();
  const clickable = step.kind === 'active';
  const status =
    step.kind === 'prefilledView'
      ? t('setupWizard.viewOnly')
      : step.complete
        ? t('setupWizard.complete')
        : active
          ? t('setupWizard.inProgress')
          : t('setupWizard.todo');

  return (
    <Box
      component={clickable ? 'button' : 'div'}
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onSelect : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        width: '100%',
        textAlign: 'left',
        border: 'none',
        borderRadius: 2,
        p: 1.25,
        cursor: clickable ? 'pointer' : 'default',
        fontFamily: 'inherit',
        bgcolor: active ? '#FFF1E8' : 'transparent',
        '&:hover': clickable ? { bgcolor: active ? '#FFF1E8' : '#f3f5f7' } : undefined,
      }}
    >
      <Bullet done={step.complete} active={active} index={index + 1} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 700, color: active ? '#C2410C' : '#1f2733' }}>{t(`setup.steps.${step.key}`)}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: step.complete ? GREEN : active ? ORANGE : GREY }}>{status}</Typography>
        </Box>
      </Box>
    </Box>
  );
}
function Bullet({ done, active, index }: { done: boolean; active: boolean; index: number }) {
  if (done) {
    return (
      <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name="check" size={15} color="white" ariaLabel="" />
      </Box>
    );
  }
  return (
    <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: active ? ORANGE : '#eef1f4', color: active ? '#fff' : '#8a93a3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 13, fontWeight: 700 }}>
      {index}
    </Box>
  );
}

// ─── Step 5: Storefront (functional) ─────────────────────────────────────────

function StorefrontStep() {
  const { t } = useTranslation();
  const { activeVendorId } = useVendor();
  const { profile, refresh } = useSetup();
  const [logo, setLogo] = useState<string | undefined>(profile?.photosJson?.logo);
  const [photos, setPhotos] = useState<string[]>(profile?.photosJson?.photos ?? []);
  const [uploadingKind, setUploadingKind] = useState<'logo' | 'photo' | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async (kind: 'logo' | 'photo', file: File) => {
    if (!activeVendorId) return;
    if (file.size > 4 * 1024 * 1024) {
      setError(t('setupWizard.storefront.imageTooLarge'));
      return;
    }
    setUploadingKind(kind);
    setError(null);
    setSaved(false);
    try {
      const { cdnUrl } = await vendorApiClient.uploadAsset(activeVendorId, kind, file);
      if (kind === 'logo') setLogo(cdnUrl);
      else setPhotos([cdnUrl]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploadingKind(null);
    }
  };

  if (!profile) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <Spinner color="brand" />
      </Box>
    );
  }

  const save = async () => {
    if (!activeVendorId) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await vendorApiClient.updateProfile(activeVendorId, {
        photosJson: {
          ...(profile.photosJson ?? {}),
          logo: logo || undefined,
          photos: photos.length ? photos : undefined,
        },
      });
      refresh();
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>{t('setupWizard.storefront.title')}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{t('setupWizard.storefront.intro')}</Typography>
      </Box>

      {/* Logo + store photo upload */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <UploadZone label={t('setupWizard.storefront.addLogo')} title={t('setupWizard.storefront.logo')} width={120} currentUrl={logo} uploading={uploadingKind === 'logo'} onPick={(f) => handleUpload('logo', f)} />
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <UploadZone label={t('setupWizard.storefront.addStorePhoto')} title={t('setupWizard.storefront.storePhoto')} currentUrl={photos[0]} uploading={uploadingKind === 'photo'} onPick={(f) => handleUpload('photo', f)} />
        </Box>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {saved && <Alert severity="success" onClose={() => setSaved(false)}>{t('setupWizard.storefront.saved')}</Alert>}

      <Box>
        <Button ariaLabel={t('common.saveChanges')} buttonText={saving ? t('common.saving') : t('common.saveChanges')} variant="contained" onClick={save} disabled={saving} />
      </Box>
    </Box>
  );
}

function UploadZone({
  label,
  title,
  width,
  currentUrl,
  uploading,
  onPick,
}: {
  label: string;
  title: string;
  width?: number;
  currentUrl?: string;
  uploading?: boolean;
  onPick: (file: File) => void;
}) {
  return (
    <Box sx={{ width }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#3a4350', mb: 0.75 }}>{title}</Typography>
      <Box
        component="label"
        sx={{
          position: 'relative',
          height: 120,
          border: '1.5px dashed #cbd2da',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          color: GREY,
          bgcolor: '#fafbfc',
          cursor: uploading ? 'default' : 'pointer',
          overflow: 'hidden',
        }}
      >
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          disabled={uploading}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onPick(f);
            e.target.value = '';
          }}
        />
        {uploading ? (
          <Spinner color="brand" />
        ) : currentUrl ? (
          <Box component="img" src={currentUrl} alt={title} sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 1 }} />
        ) : (
          <>
            <Icon name="add" size={22} ariaLabel="" />
            <Typography sx={{ fontSize: 13, color: GREY }}>{label}</Typography>
          </>
        )}
      </Box>
    </Box>
  );
}

// ─── Step 6: Import (Figma-faithful preview) ─────────────────────────────────

function ImportStep() {
  const { t } = useTranslation();
  const sources = [
    { key: 'spreadsheet', icon: 'list' as const, color: GREEN, tint: '#E3F4E7', cta: 'uploadFile' },
    { key: 'pos', icon: 'store' as const, color: '#343F5B', tint: '#EEF1F8', cta: 'connect', connected: true },
    { key: 'api', icon: 'devices' as const, color: '#07BDC4', tint: '#E9FBFC', cta: 'setUp' },
    { key: 'manual', icon: 'add' as const, color: ORANGE, tint: '#FFF1E3', cta: 'start' },
  ];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>{t('setupWizard.import.title')}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{t('setupWizard.import.intro')}</Typography>
      </Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#8a93a3' }}>{t('setupWizard.import.chooseSource')}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        {sources.map((s) => (
          <Box key={s.key} sx={{ border: '1px solid #e2ebe4', borderRadius: 3, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: s.tint, color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name={s.icon} size={20} color={s.color} ariaLabel="" />
              </Box>
              <Typography sx={{ fontWeight: 800 }}>{t(`setupWizard.import.${s.key}`)}</Typography>
              {s.connected && <Chip label={t('setupWizard.import.connected')} size="small" backgroundColor="#E3F4E7" textColor="#1B7A33" fontSize={11} icon="check" />}
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>{t(`setupWizard.import.${s.key}Body`)}</Typography>
            <Typography sx={{ color: s.color, fontWeight: 700, fontSize: 14 }}>{t(`setupWizard.import.${s.cta}`)} →</Typography>
          </Box>
        ))}
      </Box>
      <Alert severity="info">{t('setupWizard.import.deferredNote')}</Alert>
    </Box>
  );
}

// ─── Step 7: Confirm (Figma-faithful preview) ────────────────────────────────

function ConfirmStep() {
  const { t } = useTranslation();
  const rows = [
    { name: 'Carne Asada (thin)', dept: 'Meat', match: 'Carne Asada · thin-cut beef', kind: 'strong' as const },
    { name: 'Queso Fresco 12oz', dept: 'Dairy', match: 'Queso Fresco · fresh cheese', kind: 'strong' as const },
    { name: 'Chorizo Verde', dept: 'Meat', match: 'Chorizo · green / pork', kind: 'review' as const },
    { name: "Abuela's house rub", dept: 'Pantry', match: null, kind: 'none' as const },
  ];
  const badge = (kind: 'strong' | 'review' | 'none') =>
    kind === 'strong'
      ? { label: t('setupWizard.confirm.strongMatch'), bg: '#E3F4E7', fg: '#1B7A33' }
      : kind === 'review'
        ? { label: t('setupWizard.confirm.reviewMatch'), bg: '#FEF3E2', fg: '#B45309' }
        : { label: t('setupWizard.confirm.noMatch'), bg: '#eef0f3', fg: '#8a93a3' };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>{t('setupWizard.confirm.title')}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{t('setupWizard.confirm.intro')}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography sx={{ fontWeight: 700 }}>{t('setupWizard.confirm.needConfirm', { count: 8, total: 142 })}</Typography>
        <Chip label={t('setupWizard.confirm.confirmAllStrong')} clickable backgroundColor="#E3F4E7" textColor="#1B7A33" fontSize={13} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {rows.map((r) => {
          const b = badge(r.kind);
          return (
            <Box key={r.name} sx={{ display: 'flex', alignItems: 'center', gap: 2, border: '1px solid #e2ebe4', borderRadius: 2, px: 2, py: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ minWidth: 160, flex: '1 1 160px' }}>
                <Typography sx={{ fontWeight: 700 }}>{r.name}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('setupWizard.confirm.yourImport', { dept: r.dept })}</Typography>
              </Box>
              <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                {r.match ? <Typography sx={{ color: '#1f2733' }}>→ {r.match}</Typography> : <Typography sx={{ color: GREY }}>{t('setupWizard.confirm.noMatch')}</Typography>}
                <Chip label={r.match ? b.label : t('setupWizard.confirm.createNew')} size="small" backgroundColor={b.bg} textColor={b.fg} fontSize={11} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {r.kind === 'none' ? (
                  <Typography sx={{ color: ORANGE, fontWeight: 700, fontSize: 14 }}>{t('setupWizard.confirm.review')}</Typography>
                ) : (
                  <>
                    <Chip label={t('setupWizard.confirm.confirm')} clickable backgroundColor={GREEN} textColor="#fff" fontSize={13} />
                    <Typography sx={{ color: '#5b6573', fontWeight: 700, fontSize: 14 }}>{t('setupWizard.confirm.change')}</Typography>
                  </>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      <Alert severity="info">{t('setupWizard.confirm.deferredNote')}</Alert>
    </Box>
  );
}
